/**
 * SignDocumentPage duck — Push 8 Phase A.4c.
 *
 * Manages the public signing-link verification flow. The container reads
 * the opaque token from the route param (`/sign/:token`), the loadData
 * thunk hits POST /api/sign-link/verify, and on success the slice exposes
 * the link / template / transaction context for the Preliminary page to
 * render.
 *
 * Failure-mode error codes (NOT_FOUND / EXPIRED / REVOKED / INVALID /
 * SERVER_ERROR) drive full-page error variants in SignDocumentPage.js.
 * The mapping uses the response body's `error` string rather than the HTTP
 * status code — the body strings are documented in
 * server/api/sign-link-verify.js's response table and are unique per
 * failure mode, so they discriminate cleanly without a separate util/api.js
 * change to capture the HTTP status on thrown errors.
 *
 * No persistence: page refresh re-runs the verification (idempotent), and
 * Preliminary captures live in the parent component's React state per the
 * locked stateless flow.
 */

import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { storableError } from '../../util/errors';
import { verifySignLink, submitSignLink } from '../../util/api';

// Error code constants. Mirrored in:
//   - SignDocumentPage.js (full-page error variant rendering)
//   - SignDocumentDocumentPage.js (submit error rendering)
//   - src/translations/en.json (SignDocumentPage.error.<code>.heading/body
//     + SignDocumentPage.document.submitError.<code>)
// Pattern follows the codebase's UPPER_SNAKE_CASE for enum-style values.
//
// A.4d additions: AUTH_REQUIRED + AUTH_MISMATCH for the Contract auth gate
// at submit time, VALIDATION_FAILED for client-side body validation
// rejection at the server. SERVER_ERROR remains the catch-all.
export const SIGN_LINK_ERROR_CODES = {
  NOT_FOUND: 'NOT_FOUND',
  EXPIRED: 'EXPIRED',
  REVOKED: 'REVOKED',
  INVALID: 'INVALID',
  SERVER_ERROR: 'SERVER_ERROR',
  AUTH_REQUIRED: 'AUTH_REQUIRED',
  AUTH_MISMATCH: 'AUTH_MISMATCH',
  VALIDATION_FAILED: 'VALIDATION_FAILED',
};

// Map response body's `error` string → loadError/submitError code. Anything
// that doesn't match a known string (network failure, unexpected 500, etc.)
// falls through to SERVER_ERROR — the broadest, most user-friendly catch-all.
//
// Verify endpoint codes: Not found / Expired / Revoked / Invalid token / Server error.
// Submit endpoint additionally returns: Validation failed,
// "Authentication required for contract signing", "Authentication mismatch ...".
const mapErrorBodyToCode = errBody => {
  if (typeof errBody !== 'string') return SIGN_LINK_ERROR_CODES.SERVER_ERROR;
  if (errBody === 'Not found') return SIGN_LINK_ERROR_CODES.NOT_FOUND;
  if (errBody === 'Expired') return SIGN_LINK_ERROR_CODES.EXPIRED;
  if (errBody === 'Revoked') return SIGN_LINK_ERROR_CODES.REVOKED;
  if (errBody === 'Invalid token') return SIGN_LINK_ERROR_CODES.INVALID;
  if (errBody === 'Validation failed') return SIGN_LINK_ERROR_CODES.VALIDATION_FAILED;
  if (errBody.startsWith('Authentication required'))
    return SIGN_LINK_ERROR_CODES.AUTH_REQUIRED;
  if (errBody.startsWith('Authentication mismatch'))
    return SIGN_LINK_ERROR_CODES.AUTH_MISMATCH;
  return SIGN_LINK_ERROR_CODES.SERVER_ERROR;
};

// ================ Async thunk ================ //

const loadSignLinkPayloadCreator = async (token, { rejectWithValue }) => {
  try {
    const response = await verifySignLink(token);
    return response;
  } catch (e) {
    // src/util/api.js's request() throws an Error with the body fields
    // merged in via Object.assign, so e.error is the response body's
    // `error` string for our 4xx/5xx responses, or undefined on network
    // failures. mapErrorBodyToCode defaults to SERVER_ERROR for all
    // non-matching cases.
    const code = mapErrorBodyToCode(e?.error);
    return rejectWithValue({
      code,
      // storableError preserves the underlying error object's name/message
      // for debugging while staying Redux-storable.
      detail: storableError(e),
    });
  }
};
export const loadSignLinkThunk = createAsyncThunk(
  'app/SignDocumentPage/loadSignLink',
  loadSignLinkPayloadCreator
);

// A.4d submit thunk. Posts the assembled signing payload to /api/sign-link/
// submit. Success returns { documentInstanceId, submittedAt } which the
// reducer stores so the success page (route /sign/:token/success) can read
// it from Redux without re-fetching. Failure returns a code + storable
// error detail; the Document page renders the matching submitError.<code>
// translation key and offers the user a clear next step.
const submitSignLinkPayloadCreator = async (body, { rejectWithValue }) => {
  try {
    const response = await submitSignLink(body);
    return response;
  } catch (e) {
    const code = mapErrorBodyToCode(e?.error);
    return rejectWithValue({
      code,
      detail: storableError(e),
    });
  }
};
export const submitSignLinkThunk = createAsyncThunk(
  'app/SignDocumentPage/submitSignLink',
  submitSignLinkPayloadCreator
);

// loadData(params) — params.token comes from the route /sign/:token.
// Used by pageDataLoadingAPI's SignDocumentPage entry. Returns the unwrapped
// thunk result (or throws via rejectWithValue if loading fails) so the
// route loader can chain on it; the slice already absorbs the error into
// state.loadError, so the loader-side throw is just for observability.
export const loadData = (params /* , search, config */) => dispatch => {
  const token = params?.token;
  if (!token) {
    // No token in URL — slice transitions to NOT_FOUND. unwrap() throws
    // with the rejected payload so caller can log if desired.
    return dispatch(loadSignLinkThunk('')).unwrap();
  }
  return dispatch(loadSignLinkThunk(token)).unwrap();
};

// ================ Slice ================ //

const initialState = {
  loadInProgress: false,
  loadError: null, // { code, detail } | null
  link: null,
  template: null,
  transaction: null,
  // A.4f — provider-supplied overrides for this transaction. Threaded
  // through to the signingTimeResolver via context so chip resolution
  // checks overrides FIRST before listing/booking lookups. Empty
  // object when no overrides exist (common path).
  reservationFieldOverrides: {},
  // A.4d submit state. Populated by submitSignLinkThunk.
  submitInProgress: false,
  submitError: null,    // { code, detail } | null
  submitResponse: null, // { documentInstanceId, submittedAt } | null
};

const slice = createSlice({
  name: 'SignDocumentPage',
  initialState,
  reducers: {
    // Reset action — useful when the user navigates away from the page
    // (e.g., to /sign/:other-token) so stale state doesn't bleed across
    // mounts. Optional; loadData also wipes via .pending below.
    resetSignDocumentPage: () => initialState,
  },
  extraReducers: builder => {
    builder
      .addCase(loadSignLinkThunk.pending, state => {
        state.loadInProgress = true;
        state.loadError = null;
        state.link = null;
        state.template = null;
        state.transaction = null;
        state.reservationFieldOverrides = {};
      })
      .addCase(loadSignLinkThunk.fulfilled, (state, action) => {
        state.loadInProgress = false;
        state.loadError = null;
        state.link = action.payload?.link || null;
        state.template = action.payload?.template || null;
        state.transaction = action.payload?.transaction || null;
        // A.4f — defensive shape coercion; server should always return
        // an object, but fall back to {} on any malformed payload so
        // the resolver doesn't NPE on a non-object access.
        state.reservationFieldOverrides =
          action.payload?.reservationFieldOverrides &&
          typeof action.payload.reservationFieldOverrides === 'object'
            ? action.payload.reservationFieldOverrides
            : {};
      })
      .addCase(loadSignLinkThunk.rejected, (state, action) => {
        state.loadInProgress = false;
        // action.payload is the { code, detail } object from rejectWithValue.
        // Fall back to SERVER_ERROR for unhandled rejection paths.
        state.loadError =
          action.payload || {
            code: SIGN_LINK_ERROR_CODES.SERVER_ERROR,
            detail: null,
          };
        state.link = null;
        state.template = null;
        state.transaction = null;
        state.reservationFieldOverrides = {};
      })
      // ── A.4d submit thunk ──────────────────────────────────────────────
      .addCase(submitSignLinkThunk.pending, state => {
        state.submitInProgress = true;
        state.submitError = null;
        state.submitResponse = null;
      })
      .addCase(submitSignLinkThunk.fulfilled, (state, action) => {
        state.submitInProgress = false;
        state.submitError = null;
        state.submitResponse = action.payload || null;
      })
      .addCase(submitSignLinkThunk.rejected, (state, action) => {
        state.submitInProgress = false;
        state.submitError =
          action.payload || {
            code: SIGN_LINK_ERROR_CODES.SERVER_ERROR,
            detail: null,
          };
        state.submitResponse = null;
      });
  },
});

export const { resetSignDocumentPage } = slice.actions;
export default slice.reducer;
